import { useTheme } from "@/lib/useTheme";
import { Moon, Sun } from "@phosphor-icons/react";

/**
 * Two-way theme toggle for the Settings page. Dark ↔ Light. Persists to the
 * user profile via `useTheme` so the choice follows the partner across
 * devices. The palettes are deliberately not inversions:
 *   • Dark  — Navy Ink surface, Stone borders, Ivory text.
 *   • Light — Ivory reading surface, Stone structure, Navy primary, Black text.
 */
export default function ThemeToggleCard() {
  const { theme, setTheme, syncedToServer } = useTheme();

  return (
    <div className="border border-border bg-card p-6 md:col-span-2" data-testid="theme-toggle-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            {theme === "dark" ? <Moon size={12} weight="fill" /> : <Sun size={12} weight="fill" />} Interface theme
          </div>
          <div className="mt-1 text-sm text-muted-foreground max-w-md">
            Dark is Navy Ink for late nights and long screens. Light is Ivory for editorial reads.
            <span className="block text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow/70 mt-1" data-testid="theme-sync-status">
              {syncedToServer
                ? "Synced to your profile · follows you across devices"
                : "Stored on this device"}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          onClick={() => setTheme("dark")}
          data-testid="theme-option-dark"
          className={`text-left border p-4 transition ${
            theme === "dark"
              ? "border-primary bg-primary/10"
              : "border-border/60 bg-secondary/20 hover:border-primary/60"
          }`}
        >
          <div className="flex items-center gap-2">
            <Moon size={16} weight={theme === "dark" ? "fill" : "regular"} className={theme === "dark" ? "text-primary" : "text-muted-foreground"} />
            <div className={`text-[0.625rem] font-mono uppercase tracking-widest ${theme === "dark" ? "text-eyebrow" : "text-muted-foreground"}`}>Dark · Navy Ink</div>
          </div>
          <div className="mt-1 text-sm">Deep navy surface with lifted card tiers and warm ivory text.</div>
        </button>
        <button
          onClick={() => setTheme("light")}
          data-testid="theme-option-light"
          className={`text-left border p-4 transition ${
            theme === "light"
              ? "border-primary bg-primary/10"
              : "border-border/60 bg-secondary/20 hover:border-primary/60"
          }`}
        >
          <div className="flex items-center gap-2">
            <Sun size={16} weight={theme === "light" ? "fill" : "regular"} className={theme === "light" ? "text-primary" : "text-muted-foreground"} />
            <div className={`text-[0.625rem] font-mono uppercase tracking-widest ${theme === "light" ? "text-eyebrow" : "text-muted-foreground"}`}>Light · Ivory</div>
          </div>
          <div className="mt-1 text-sm">Ivory reading surface with Stone structure, Navy primary and Black text.</div>
        </button>
      </div>
    </div>
  );
}
